import { computed, type ComputedRef, nextTick, type Reactive, reactive, ref, type Ref, watch } from 'vue'
import { defineStore } from 'pinia'
import type { CustomFieldDefinition, Response } from '@/models/api-generated'
import type { OrderByField } from '@/models/filters-generated'
import http from '@/services/http'
import type { PaginationState } from '@/types/page'

export interface Column {
	name: string
	label: string
	filter?: any | any[]
	order?: 'asc' | 'desc'
	isVisible: boolean
	customField?: CustomFieldDefinition
	toggleOrder(): void
}

export interface ColumnRoot {
	pagination: Reactive<PaginationState>
	filterValue(): any
	orderValue(): OrderByField | undefined
	triggerFetch(): void
}

interface component {
	fetch(): void
	fetchCallback?: (abort: AbortSignal) => Promise<void>
	wait?: Promise<void>
	columns: Map<string, Ref<Column>>
	order?: OrderByField
	pagination: Reactive<PaginationState>
	abortController?: AbortController
}

export default defineStore('column', () => {
	const components = new Map<string, component>()

	function defineComponentPrivate(component: string) {
		let comp = components.get(component)
		if (!comp) {
			comp = {
				columns: new Map<string, Ref<Column>>(),
				pagination: reactive<PaginationState>({ page: 1, limit: 50 }),
				fetch() {
					if (!this.wait) {
						this.wait = nextTick()
							.then(() => {
								if (this.fetchCallback) {
									this.abortController?.abort()
									this.abortController = new AbortController()
									this.fetchCallback(this.abortController.signal).finally(() => (this.abortController = undefined))
								}
							})
							.finally(() => (this.wait = undefined))
					}
				},
			}
			components.set(component, comp)
		}
		return comp
	}

	function defineComponent(component: string, fetchCallback?: (abort: AbortSignal) => Promise<void>, init: boolean = true) {
		const comp = defineComponentPrivate(component)
		if (fetchCallback) {
			if (comp.fetchCallback) console.warn('probable unintentional overriding of fetchCallback')
			comp.fetchCallback = fetchCallback
		}

		const root: ColumnRoot = {
			pagination: comp.pagination,
			filterValue() {
				const flt: { [key: string]: any } = {}
				comp.columns.forEach((v, k) => {
					if (v.value.filter?.length) {
						if (!v.value.customField) flt[k] = v.value.filter
						else {
							if (!flt['custom']) flt['custom'] = {}
							flt['custom'][k.replace('__custom_', '')] = v.value.filter
						}
					}
				})
				return flt
			},
			orderValue() {
				return comp.order
			},
			triggerFetch() {
				comp.pagination.page = 1
				comp.fetch()
			},
		}

		watch(comp.pagination, () => comp.fetch(), { deep: true })

		if (init) nextTick(() => comp.fetch())

		return root
	}

	function define(component: string, column: string, label: string, isVisible?: boolean, customField?: CustomFieldDefinition) {
		const comp = defineComponentPrivate(component)
		let col = comp.columns.get(column)
		if (col) return col

		const key = `${component}-column-visibility`
		const loadJson = localStorage.getItem(key)
		let loadNames: string[] | undefined
		if (loadJson) loadNames = JSON.parse(loadJson)

		col = ref<Column>({
			name: column,
			label,
			filter: [],
			isVisible: loadNames?.includes(column) ?? isVisible ?? false,
			customField,
			toggleOrder: () => toggleOrder(component, column),
		})

		watch(
			() => col.value.isVisible,
			() => {
				const saveNames = Array.from(comp.columns.values())
					.filter((x) => x.value.isVisible)
					.map((x) => x.value.name)
				const saveJson = JSON.stringify(saveNames)
				localStorage.setItem(key, saveJson)
			},
		)

		comp.columns.set(column, col)

		watch(col.value, () => {
			comp.pagination.page = 1
			comp.fetch()
		})
		return col
	}

	async function defineCustom(component: string, tables: string[]) {
		const columns: Ref<Column>[] = []

		for (const t of tables) {
			const { data } = await http.get<Response<CustomFieldDefinition[]>>(`api/customFields/${t}`)
			if (!data.data?.length) continue

			const key = `${component}-custom-column-visibility`
			const loadJson = localStorage.getItem(key)
			let loadNames: string[] | undefined
			if (loadJson) loadNames = JSON.parse(loadJson)

			for (const a of data.data) {
				const name = `__custom_${a.id}`
				const cf = define(component, name, a.label, loadNames?.includes(name), a)
				columns.push(cf)

				watch(
					() => cf.value.isVisible,
					() => {
						const saveNames = columns.filter((x) => x.value.isVisible).map((x) => x.value.name)
						const saveJson = JSON.stringify(saveNames)
						localStorage.setItem(key, saveJson)
					},
				)
			}
		}

		return columns
	}

	function toggleOrder(component: string, column: string) {
		const comp = defineComponentPrivate(component)
		const col = comp.columns.get(column)
		if (!col) return

		comp.columns.forEach((v, k) => {
			if (k !== column) v.value.order = undefined
		})

		if (col.value.order === 'asc') col.value.order = 'desc'
		else if (col.value.order === 'desc') col.value.order = undefined
		else col.value.order = 'asc'

		if (col.value.order) {
			if (!col.value.customField) {
				comp.order = {
					field: col.value.name,
					order: col.value.order,
				}
			} else {
				comp.order = {
					field: col.value.customField.id.toString(),
					order: col.value.order,
				}
			}
		} else {
			delete comp.order
		}
	}

	const persists = new Map<string, { custom: Ref<Ref<Column>[]>; all: ComputedRef<Ref<Column>[]> }>()

	function finalize(component: string, columns: { [name: string]: Ref<Column> }, hasCustomFields?: boolean, customTables?: string[]) {
		let pers = persists.get(component)
		if (pers) return pers

		const custom = ref<Ref<Column>[]>([])

		if (hasCustomFields) defineCustom(component, customTables ?? [component]).then((x) => custom.value.push(...x))

		const all = computed(() => {
			return [...Object.values(columns), ...custom.value]
		})

		pers = { custom, all }
		persists.set(component, pers)

		return pers
	}

	return { defineComponent, define, finalize }
})
